import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../context/alert/AlertContext';
import './MessageComponent.css';

/**
 * Floating bottom-right toast that surfaces the global alert.
 *
 * Renders nothing when there's no message. When a message is present it
 * slides in from the right with a coloured left border and an icon, then
 * auto-dismisses (success after 4s, danger after 10s — same as before).
 *
 * The underlying AlertContext stays single-message; if we ever need true
 * stacking, that's the place to switch to an array.
 */
const MessageComponent: React.FC = () => {
    const { t } = useTranslation();
    const { alertMessage, alertType, clearAlert } = useAlert();

    useEffect(() => {
        if (alertMessage) {
            const timer = setTimeout(() => {
                clearAlert();
            }, alertType === 'success' ? 4000 : 10000);

            return () => clearTimeout(timer);
        }
    }, [alertMessage, alertType, clearAlert]);

    if (!alertMessage || !alertType) return null;

    const icon = alertType === 'success' ? '✓' : '⚠';

    return (
        <div className="gs-toast-container" role="status" aria-live="polite" data-cy="message-toast">
            <div
                className={`gs-toast gs-toast--${alertType}`}
                /* key makes the animation replay when the message changes
                 * (e.g. one error toast immediately followed by another). */
                key={alertMessage}
                data-cy={`message-${alertType}`}
            >
                <span className="gs-toast__icon" aria-hidden="true">{icon}</span>
                <div className="gs-toast__body">{alertMessage}</div>
                <button
                    type="button"
                    className="gs-toast__close"
                    aria-label={t('common.dismiss')}
                    onClick={clearAlert}
                >
                    ×
                </button>
            </div>
        </div>
    );
};

export default MessageComponent;
